import '../index.scss';

import { useCallback, useEffect, useState } from 'react';
import { Spin } from 'antd';
import dayjs from 'dayjs';

import { requestWithAuth } from '../../services/api';
import CommonTable from '../../components/CommonTable';
import TopSection from '../../components/TopSection';
import ExportCsv from '../../components/ExportCsv';

interface RequestParams {
  page_size: number;
  page: number;
  search?: string;
  order_by?: string;
}

interface SortInfo {
  column: string;
  order: 'ascend' | 'descend' | '';
}

const DEFAULT_SORT: SortInfo = { column: 'accepted_at', order: 'descend' };

const getOrderBy = (sortInfo: SortInfo) => {
  if (!sortInfo.column || !sortInfo.order) return undefined;
  return sortInfo.order === 'descend' ? `-${sortInfo.column}` : sortInfo.column;
};

function TermsAcceptance() {
  const [listPagination, setListPagination] = useState({
    total: 0,
    pageSize: 50,
    current: 1,
  });
  const { pageSize, current } = listPagination;
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortInfo, setSortInfo] = useState<SortInfo>(DEFAULT_SORT);
  const [termsData, setTermsData] = useState([]);
  const [termsDataCount, setTermsDataCount] = useState(0);

  const fetchTermsData = useCallback(() => {
    setIsLoading(true);
    const params: RequestParams = {
      page_size: pageSize,
      page: current,
    };
    if (searchQuery) params.search = searchQuery;
    const orderBy = getOrderBy(sortInfo);
    if (orderBy) params.order_by = orderBy;

    requestWithAuth('vault-list-terms-acceptance', 'GET', params, null)
      .then((response) => {
        if (response && response.results) {
          setTermsData(response.results);
          setTermsDataCount(response.count);
          setListPagination((prevState) => ({
            ...prevState,
            total: response.count,
          }));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [pageSize, current, searchQuery, sortInfo]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchTermsData();
  }, [fetchTermsData]);

  const handleTableChange = (pagination, _filters, sorter) => {
    if (sorter && sorter.order && sorter.columnKey) {
      setSortInfo({ column: sorter.columnKey, order: sorter.order });
    } else {
      setSortInfo({ column: '', order: '' });
    }
    setListPagination({
      ...pagination,
      pageSize: pagination.pageSize,
      current: pagination.current,
    });
  };

  const handleSearchQuery = (searchValue) => {
    setSearchQuery(searchValue || '');
    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));
  };

  const handleFormReset = () => {
    setSortInfo(DEFAULT_SORT);
    setSearchQuery('');
    setListPagination({
      current: 1,
      pageSize: 50,
      total: 0,
    });
  };

  const columns = [
    {
      title: 'User Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'email' && sortInfo.order,
    },
    {
      title: 'Accepted At',
      dataIndex: 'accepted_at',
      key: 'accepted_at',
      width: 180,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'accepted_at' && sortInfo.order,
      render: (val) => (val ? dayjs(val).format('MM/DD/YYYY hh:mm A') : ''),
    },
    {
      title: 'Terms URL',
      dataIndex: 'terms_url',
      key: 'terms_url',
      width: 400,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortInfo.column === 'terms_url' && sortInfo.order,
      render: (val) =>
        val ? (
          <a href={val} target="_blank" rel="noreferrer">
            {val}
          </a>
        ) : (
          ''
        ),
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 150,
    },
    {
      title: '',
      dataIndex: 'dummy',
      key: 'dummy',
      width: 'auto',
    },
  ];

  const renderExportComponent = () => {
    const filterParams: Omit<RequestParams, 'page_size' | 'page'> = {};
    if (searchQuery) filterParams.search = searchQuery;
    const orderBy = getOrderBy(sortInfo);
    if (orderBy) filterParams.order_by = orderBy;

    return (
      <ExportCsv
        totalCount={termsDataCount}
        filterParams={filterParams}
        url="vault-list-terms-acceptance"
        downloadFileName="Terms_Acceptance"
      />
    );
  };

  return (
    <div className="people-editor">
      <TopSection
        count={termsDataCount}
        placeholder="Search user email"
        showNewButton={false}
        showSearch
        handleSearchQuery={handleSearchQuery}
        handleFormReset={handleFormReset}
        showExportCsv={termsDataCount > 0}
        renderExportComponent={renderExportComponent}
        searchValue={searchQuery}
      />
      <Spin size="default" spinning={isLoading}>
        <CommonTable
          columns={columns}
          data={termsData}
          count={termsDataCount}
          onTableChange={handleTableChange}
          pagination={{ ...listPagination }}
          pageSize={listPagination.pageSize}
          loading={isLoading}
          rowKey={(rec) => `${rec.email}-${rec.accepted_at}`}
        />
      </Spin>
    </div>
  );
}

export default TermsAcceptance;
